import { Injectable, OnDestroy } from '@angular/core';
import { AnexoLoaService } from '../service/anexo-loa.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FuncaoService, LoginContabil, Exercicio, Relatorio, GlobalService } from 'eddydata-lib';

@Injectable({
  providedIn: 'root'
})
export class Anexo11 implements OnDestroy {

  protected funcaoService: FuncaoService;
  private login: LoginContabil = new LoginContabil();
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected anexoServico: AnexoLoaService) {
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // tslint:disable: max-line-length
  public montarRelatorio(exercicio: Exercicio, orgaos: number[]) {
    this.anexoServico.obterAnexo11(exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {
        Relatorio.imprimirPersonalizado(
          '', this.login.usuario.nome, this.login.usuario.sobrenome,
          this.login.orgao.nome, this.login.brasao, this.conteudo(dados, exercicio),
          'landscape', 'ANEXO XI - COMPARATIVO DA DESPESA AUTORIZADA COM A REALIZADA',
          {
            linhas: {
              hLineWidth() { return 1 },
              vLineWidth() { return 1 },
              hLineColor() { return 'black' },
              paddingLeft() { return 3 },
              paddingRight() { return 3 }
            }
          }
        );
      });
  }

  private conteudo(dados: any[], exercicio: Exercicio): {}[] {
    let totalOrcado = 0;
    let totalEmpenhado = 0;
    let totalAtualizado = 0;

    const registros: {}[] = [
      [
        { text: `ANEXO XI - COMPARATIVO DA DESPESA AUTORIZADA COM A REALIZADA - Exercício: ${exercicio.ano}`, alignment: 'center', bold: true, fontSize: 10, colSpan: 6, border: [false, false, false, false], margin: [0, -15, 0, 0] },
        '', '', '', '', ''
      ],
      [
        { text: 'COMPARATIVO DA DESPESA AUTORIZADA COM A REALIZADA', alignment: 'center', bold: true, fontSize: 10, colSpan: 6, border: [true, true, true, true] },
        '', '', '', '', ''
      ],
      [
        { text: 'DESCRIÇÃO', alignment: 'center', bold: true, fontSize: 9, margin: [0, 20, 0, -20], border: [true, true, true, true], rowSpan: 2 },
        { text: 'AUTORIZADA R$', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true], colSpan: 3 },
        '',
        '',
        { text: 'REALIZADA R$', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true] },
        { text: 'DIFERENÇAS', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true], margin: [0, 20, 0, -20], rowSpan: 2 },
      ],
      [
        '',
        { text: 'CRÉDITOS ORÇAMENTÁRIOS E SUPLEMENTARES', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true] },
        { text: 'CRÉDITOS ESPECIAIS E EXTRAORDINÁRIOS', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true] },
        { text: 'TOTAIS', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true], margin: [0, 12, 0, 0] },
        { text: 'EMPENHADO', alignment: 'center', bold: true, fontSize: 9, border: [true, true, true, true], margin: [0, 12, 0, 0] },
        '',
      ]
    ];

    const gruposOrgaos = this.funcaoService.agrupar(dados, ['orgao_codigo', 'orgao_nome'], ['valor_orcado', 'valor_empenhado', 'valor_atualizacao']);
    for (const orgao of gruposOrgaos) {

      const dotacaoOrgaoTotal = +(orgao.totalizadores['valor_orcado'] ?? 0) + +(orgao.totalizadores['valor_atualizacao'] ?? 0);
      const diferencaOrgao = +dotacaoOrgaoTotal - +(orgao.totalizadores['valor_empenhado'] ?? 0);
      registros.push([
        { text: `${orgao.grupo['orgao_codigo']} - ${orgao.grupo['orgao_nome']}`, bold: true, fontSize: 9, border: [true, false, true, false], decoration: 'underline' },
        { text: this.funcaoService.convertToBrNumber(orgao.totalizadores['valor_orcado'], 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false], decoration: 'underline' },
        { text: this.funcaoService.convertToBrNumber(orgao.totalizadores['valor_atualizacao'], 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false], decoration: 'underline' },
        { text: this.funcaoService.convertToBrNumber(dotacaoOrgaoTotal, 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false], decoration: 'underline' },
        { text: this.funcaoService.convertToBrNumber(orgao.totalizadores['valor_empenhado'], 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false], decoration: 'underline' },
        { text: this.funcaoService.convertToBrNumber(diferencaOrgao, 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false], decoration: 'underline' },
      ]);

      const gruposUnidades = this.funcaoService.agrupar(orgao.registros, ['unidade_codigo', 'unidade_nome'], ['valor_orcado', 'valor_empenhado', 'valor_atualizacao']);
      for (const unidade of gruposUnidades) {

        const dotacaoUnidadeTotal = +(unidade.totalizadores['valor_orcado'] ?? 0) + +(unidade.totalizadores['valor_atualizacao'] ?? 0);
        const diferencaUnidade = +dotacaoUnidadeTotal - +(unidade.totalizadores['valor_empenhado'] ?? 0);
        registros.push([
          { text: `${unidade.grupo['unidade_codigo']} - ${unidade.grupo['unidade_nome']}`, bold: true, fontSize: 8, border: [true, false, true, false], margin: [6, 0, 0, 0] },
          { text: this.funcaoService.convertToBrNumber(unidade.totalizadores['valor_orcado'], 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false] },
          { text: this.funcaoService.convertToBrNumber(unidade.totalizadores['valor_atualizacao'], 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false] },
          { text: this.funcaoService.convertToBrNumber(dotacaoUnidadeTotal, 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false] },
          { text: this.funcaoService.convertToBrNumber(unidade.totalizadores['valor_empenhado'], 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false] },
          { text: this.funcaoService.convertToBrNumber(diferencaUnidade, 2), alignment: 'right', bold: true, fontSize: 8, border: [true, false, true, false] },
        ]);

        const gruposClassificacao = this.funcaoService.agrupar(unidade.registros, ['classificacao_codigo', 'classificacao_nome'], ['valor_orcado', 'valor_empenhado', 'valor_atualizacao']);
        for (const classificacao of gruposClassificacao) {

          const dotacaoClassificacaoTotal = +(classificacao.totalizadores['valor_orcado'] ?? 0) + +(classificacao.totalizadores['valor_atualizacao'] ?? 0);
          const diferencaClassificacao = +dotacaoClassificacaoTotal - +(classificacao.totalizadores['valor_empenhado'] ?? 0);
          registros.push([
            { text: `${classificacao.grupo['classificacao_codigo']} - ${classificacao.grupo['classificacao_nome']}`, decoration: 'underline', fontSize: 8, margin: [12, 0, 0, 0], border: [true, false, true, false], bold: true },
            { text: this.funcaoService.convertToBrNumber(classificacao.totalizadores['valor_orcado'], 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false], bold: true },
            { text: this.funcaoService.convertToBrNumber(classificacao.totalizadores['valor_atualizacao'], 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false], bold: true },
            { text: this.funcaoService.convertToBrNumber(dotacaoClassificacaoTotal, 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false], bold: true },
            { text: this.funcaoService.convertToBrNumber(classificacao.totalizadores['valor_empenhado'], 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false], bold: true },
            { text: this.funcaoService.convertToBrNumber(diferencaClassificacao, 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false], bold: true },
          ]);

          const gruposSub = this.funcaoService.agrupar(classificacao.registros, ['sub_codigo', 'sub_nome'], ['valor_orcado', 'valor_empenhado', 'valor_atualizacao']);
          for (const sub of gruposSub) {

            const dotacaoSubTotal = +(sub.totalizadores['valor_orcado'] ?? 0) + +(sub.totalizadores['valor_atualizacao'] ?? 0);
            const diferencaSub = +dotacaoSubTotal - +(sub.totalizadores['valor_empenhado'] ?? 0);
            registros.push([
              { text: `${sub.grupo['sub_codigo']} - ${sub.grupo['sub_nome']}`, decoration: 'underline', fontSize: 8, margin: [18, 0, 0, 0], border: [true, false, true, false] },
              { text: this.funcaoService.convertToBrNumber(sub.totalizadores['valor_orcado'], 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false] },
              { text: this.funcaoService.convertToBrNumber(sub.totalizadores['valor_atualizacao'], 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false] },
              { text: this.funcaoService.convertToBrNumber(dotacaoSubTotal, 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false] },
              { text: this.funcaoService.convertToBrNumber(sub.totalizadores['valor_empenhado'], 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false] },
              { text: this.funcaoService.convertToBrNumber(diferencaSub, 2), alignment: 'right', decoration: 'underline', fontSize: 8, border: [true, false, true, false] },
            ]);

            for (const despesa of sub.registros) {

              const dotacaoSubTotal = +(despesa.valor_orcado ?? 0) + +(despesa.valor_atualizacao ?? 0);
              const diferencaDespesa = +dotacaoSubTotal - +(despesa.valor_empenhado ?? 0);
              registros.push([
                { text: `${despesa.codigo} - ${despesa.nome_despesa}`, fontSize: 8, margin: [24, 0, 0, 0], border: [true, false, true, false] },
                { text: this.funcaoService.convertToBrNumber(despesa.valor_orcado, 2), alignment: 'right', fontSize: 8, border: [true, false, true, false] },
                { text: this.funcaoService.convertToBrNumber(despesa.valor_atualizacao, 2), alignment: 'right', fontSize: 8, border: [true, false, true, false] },
                { text: this.funcaoService.convertToBrNumber(dotacaoSubTotal, 2), alignment: 'right', fontSize: 8, border: [true, false, true, false] },
                { text: this.funcaoService.convertToBrNumber(despesa.valor_empenhado, 2), alignment: 'right', fontSize: 8, border: [true, false, true, false] },
                { text: this.funcaoService.convertToBrNumber(diferencaDespesa, 2), alignment: 'right', fontSize: 8, border: [true, false, true, false] },
              ]);

              totalOrcado += +(despesa.valor_orcado ?? 0);
              totalAtualizado += +(despesa.valor_atualizacao ?? 0);
              totalEmpenhado += +(despesa.valor_empenhado ?? 0);
            }
          }
        }
      }
    }

    const totalDotacao = +(totalOrcado ?? 0) + +(totalAtualizado ?? 0);
    const diferenca = totalDotacao - +(totalEmpenhado ?? 0);
    registros.push([
      { text: 'TOTAL GERAL:', bold: true, fontSize: 9, border: [true, true, true, true], margin: [0, 2, 0, 2] },
      { text: this.funcaoService.convertToBrNumber(totalOrcado, 2), bold: true, alignment: 'right', fontSize: 8, border: [true, true, true, true], margin: [0, 2, 0, 2] },
      { text: this.funcaoService.convertToBrNumber(totalAtualizado, 2), bold: true, alignment: 'right', fontSize: 8, border: [true, true, true, true], margin: [0, 2, 0, 2] },
      { text: this.funcaoService.convertToBrNumber(totalDotacao, 2), bold: true, alignment: 'right', fontSize: 8, border: [true, true, true, true], margin: [0, 2, 0, 2] },
      { text: this.funcaoService.convertToBrNumber(totalEmpenhado, 2), bold: true, alignment: 'right', fontSize: 8, border: [true, true, true, true], margin: [0, 2, 0, 2] },
      { text: this.funcaoService.convertToBrNumber(diferenca, 2), bold: true, alignment: 'right', fontSize: 8, border: [true, true, true, true], margin: [0, 2, 0, 2] },
    ]);

    return [{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 4,
        widths: ['*', 82, 82, 80, 80, 80],
        body: registros
      }
    }];
  }

}
